import { FC } from 'react'
import { useSelector } from 'react-redux'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { getSelectedCompany } from 'store/me'
import { REQUEST, PROHIBITED, VERIFIED } from 'utils/consts/companyStatus'

const ON_VERIFICATION_STATUS = 'ON_VERIFICATION'

const verificationStatusText = {
  [REQUEST]: 'company.statusBanner.request',
  [PROHIBITED]: 'company.statusBanner.prohibited',
  [ON_VERIFICATION_STATUS]: 'company.statusBanner.sideMenu.notVerified',
}
const verificationStatusColor = {
  [REQUEST]: 'rgba(117, 122, 192, 1)',
  [PROHIBITED]: 'rgba(255, 51, 41, 1)',
  [ON_VERIFICATION_STATUS]: 'rgba(204, 116, 0, 1)',
}

interface WrapperProps {
  $borderless?: boolean
  $color: string
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 8px;

  ${(props: WrapperProps) =>
    props.$borderless
      ? css`
          padding: 12px 16px;
          background: linear-gradient(
              0deg,
              rgba(204, 116, 0, 0.16) 0%,
              rgba(204, 116, 0, 0.16) 100%
            ),
            #121432;
        `
      : css`
          padding: 8px;
          border-radius: 8px;
          border: 1px solid ${(p: WrapperProps) => p.$color};
          color: ${(p: WrapperProps) => p.$color};
          background: linear-gradient(
              0deg,
              rgba(204, 116, 0, 0.16) 0%,
              rgba(204, 116, 0, 0.16) 100%
            ),
            #121432;
        `}
`

const Text = styled.div`
  font: var(--typography-12-16-regular);
`

const Button = styled.button`
  all: unset;
  display: flex;
  padding: 4px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  border-radius: 4px;
  background: var(--other-orange, #cc7400);
  color: var(--grey-10, #e7e7ea);
  text-align: center;
  font: var(--typography-12-16-semibold);
  cursor: pointer;
`

interface VerifyNotificationProps {
  borderless?: boolean
  onVerify: () => void
}

const VerifyNotification: FC<VerifyNotificationProps> = ({
  borderless,
  onVerify,
}) => {
  const { t } = useTranslation()
  const company = useSelector(getSelectedCompany)
  const code = company?.verificationStatus?.code

  const isVerified = code === VERIFIED
  if (isVerified) return null

  const verificationStatus =
    code && [REQUEST, PROHIBITED].includes(code) ? code : ON_VERIFICATION_STATUS
  const text = t(verificationStatusText[verificationStatus])
  const showButton = verificationStatus === ON_VERIFICATION_STATUS

  return (
    <Wrapper
      $borderless={borderless}
      $color={verificationStatusColor[verificationStatus]}
    >
      <Text>{text}</Text>
      {showButton && (
        <Button type='button' onClick={onVerify}>
          {t('common.menu.verify.button')}
        </Button>
      )}
    </Wrapper>
  )
}

export default VerifyNotification
